import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Send, RotateCw, CheckCircle2, XCircle } from "lucide-react";
import { api, errMsg } from "@/lib/galleryApi";

const fmt = (d) => (d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "");

function History({ items, onResend }) {
  if (!items.length) return <p className="text-xs text-[#64748B]" data-testid="delivery-history-empty">Aucun envoi pour le moment.</p>;
  return (
    <ul className="divide-y divide-[#1A2332] max-h-56 overflow-y-auto" data-testid="delivery-history">
      {items.map((d) => (
        <li key={d.id} className="py-2 flex items-center gap-2 text-xs" data-testid={`delivery-${d.id}`}>
          {d.status === "sent" ? <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0" /> : <XCircle className="w-4 h-4 text-red-400 shrink-0" />}
          <div className="min-w-0 flex-1">
            <p className="text-white truncate">{d.email} <span className="text-[#64748B]">· {d.kind === "team" ? "equipe" : "externe"}</span></p>
            <p className="text-[#64748B] truncate">{d.status === "sent" ? "Envoye" : `Echec : ${d.error || ""}`} · {fmt(d.last_sent_at || d.created_at)}{d.attempts > 1 ? ` · ${d.attempts} envois` : ""}</p>
          </div>
          {onResend && <Button size="sm" variant="ghost" onClick={() => onResend(d)} className="h-7 text-[#0099CC]" data-testid={`resend-${d.id}`}><RotateCw className="w-3.5 h-3.5 mr-1" />Renvoyer</Button>}
        </li>
      ))}
    </ul>
  );
}

export function DeliveryDialog({ open, onOpenChange, gallery, basePath = "/galleries", client = false }) {
  const [team, setTeam] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [external, setExternal] = useState("");
  const [message, setMessage] = useState("");
  const [code, setCode] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`${basePath}/${gallery.id}/recipients`).then(setTeam).catch(() => {});
    api.get(`${basePath}/${gallery.id}/deliveries`).then(setHistory).catch(() => {});
  }, [open, gallery.id, basePath]);

  const recipients = [...picked, ...external.split(/[\s,;]+/).filter(Boolean)];
  const blocked = gallery.access_mode === "private" || !gallery.is_active;

  const send = async () => {
    setBusy(true);
    try {
      const res = await api.post(`${basePath}/${gallery.id}/deliveries`, { recipients, message, access_code: code || undefined, app_url: window.location.origin });
      const ok = res.filter((d) => d.status === "sent").length;
      toast[ok === res.length ? "success" : "warning"](`${ok}/${res.length} e-mail(s) envoye(s)`);
      setHistory((h) => [...res, ...h]);
      setPicked(new Set()); setExternal(""); setCode("");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const resend = async (d) => {
    try {
      const r = await api.post(`/galleries/deliveries/${d.id}/resend`, { app_url: window.location.origin, access_code: code || undefined });
      setHistory((h) => h.map((x) => (x.id === r.id ? r : x)));
      toast[r.status === "sent" ? "success" : "error"](r.status === "sent" ? `Invitation renvoyee a ${r.email}` : "Echec du renvoi");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0B1120] border-[#1A2332] text-white w-[calc(100vw-1.5rem)] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="delivery-dialog">
        <DialogHeader>
          <DialogTitle className="pr-8 text-left">Envoyer la galerie au client</DialogTitle>
          <DialogDescription className="text-[#94A3B8]">E-mail GLOTECH EVENT avec l'evenement, la photo de couverture, le lien, les modalites d'acces et de telechargement. L'e-mail ne donne aucun acces supplementaire : les regles de confidentialite de la galerie s'appliquent.</DialogDescription>
        </DialogHeader>
        {blocked && <p className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-md p-3" data-testid="delivery-blocked">La galerie est privee ou desactivee : choisissez un mode de partage dans « Partage & confidentialite » avant l'envoi.</p>}
        <div className="grid sm:grid-cols-2 gap-5 min-w-0">
          <div className="min-w-0">
            <p className="text-xs text-[#94A3B8] mb-2">Membres de l'equipe</p>
            <div className="max-h-40 overflow-y-auto space-y-1.5 border border-[#1A2332] rounded-md p-2" data-testid="delivery-team-list">
              {team.length === 0 && <p className="text-xs text-[#64748B]">Aucun membre</p>}
              {team.map((u) => (
                <label key={u.id} className="flex items-center gap-2 text-xs cursor-pointer min-w-0">
                  <Checkbox className="h-4 w-4 min-h-0 min-w-0 shrink-0" checked={picked.has(u.email)} onCheckedChange={(v) => setPicked((s) => { const n = new Set(s); v ? n.add(u.email) : n.delete(u.email); return n; })} data-testid={`delivery-team-${u.id}`} />
                  <span className="text-white truncate">{u.name || u.email}</span><span className="text-[#64748B] truncate">{u.email}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-[#94A3B8] mt-3 mb-1.5">Adresses externes (separees par une virgule)</p>
            <Input value={external} onChange={(e) => setExternal(e.target.value)} placeholder="client@exemple.com, responsable@exemple.com" className="bg-[#060B14] border-[#1A2332] text-white w-full min-w-0" data-testid="delivery-external-input" />
            {gallery.access_mode === "code" && (
              <>
                <p className="text-xs text-[#94A3B8] mt-3 mb-1.5">Inclure le code d'acces (facultatif — sinon communiquez-le separement)</p>
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code de la galerie" className="bg-[#060B14] border-[#1A2332] text-white" data-testid="delivery-code-input" />
              </>
            )}
          </div>
          <div className="min-w-0">
            <p className="text-xs text-[#94A3B8] mb-2">Message d'accompagnement</p>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} maxLength={2000} placeholder="Bonjour, voici les photos de votre evenement…" className="bg-[#060B14] border-[#1A2332] text-white" data-testid="delivery-message-input" />
          </div>
        </div>
        <Button onClick={send} disabled={blocked || busy || !recipients.length} className="bg-[#0099CC] hover:bg-[#0088B5] text-white w-full sm:w-auto" data-testid="delivery-send-btn">
          <Send className="w-4 h-4 mr-2" />Envoyer a {recipients.length} destinataire(s)
        </Button>
        <div className="border-t border-[#1A2332] pt-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] font-semibold mb-2">Historique des envois</p>
          <History items={history} onResend={client ? null : resend} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
